import React, { useMemo } from "react";
import { useElcCapByTech } from "../../hooks/useElcCapByTech";
import { getEnergyColor, DASHBOARD_COLORS } from "../../config/colorScheme";

/**
 * Table showing required infrastructure for a target year.
 * Pulls capacity by technology from ELC_CAP and converts GW -> MW,
 * then MW -> km² using assumed power densities:
 *  - Solar PV:        40 MW/km²
 *  - Onshore wind:     3 MW/km²
 *  - Offshore wind:    5 MW/km²  (sea area)
 */
export default function RenewableInfrastructureTable({
  scenario,
  year = 2050,
  // if your ELC_CAP values are MW, set gwScale={1/1000}; default assumes GW already
  gwScale = 1,
  className = "bg-blue-50 p-3 rounded-lg lg:w-3/5 sm:w-full align-center m-auto",
  title = "How much land is needed to supply this much clean electricity in 2050?",
}) {
  const { labels, byTech, techList, loading, error } = useElcCapByTech({
    scenario,
    from: year,
    to: year,
    years: [year],
    gwScale,
  });

  const data = useMemo(() => {
    const y = labels?.[0];
    if (!y) return [];

    const gw = (tech) => byTech.get(tech)?.get(y) ?? 0;
    const toMW = (vGW) => vGW * 1000;

    // Densities (MW per km²)
    const DENSITY = {
      solar: 40,
      onshoreWind: 3,
      offshoreWind: 5,
    };

    const rows = [
      {
        key: "offshoreWind",
        name: "Offshore Wind Farms",
        color: getEnergyColor("offshoreWind", "primary") ?? DASHBOARD_COLORS.neutral.baseline.primary,
        capacityGW: gw("offshoreWind"),
        density: DENSITY.offshoreWind,
        // sea area
        areaKm2: toMW(gw("offshoreWind")) / DENSITY.offshoreWind || 0,
        areaNote: "sea area",
      },
      {
        key: "onshoreWind",
        name: "Onshore Wind Farms",
        color: getEnergyColor("onshoreWind", "primary") ?? DASHBOARD_COLORS.neutral.baseline.primary,
        capacityGW: gw("onshoreWind"),
        density: DENSITY.onshoreWind,
        areaKm2: toMW(gw("onshoreWind")) / DENSITY.onshoreWind || 0,
      },
      {
        key: "solar",
        name: "Solar Farms",
        color: getEnergyColor("solar", "primary") ?? DASHBOARD_COLORS.neutral.baseline.primary,
        capacityGW: gw("solar"),
        density: DENSITY.solar,
        areaKm2: toMW(gw("solar")) / DENSITY.solar || 0,
      },
      // Optional extras (no density assumptions → no area calc)
      {
        key: "tidal",
        name: "Tidal Energy Projects",
        color: getEnergyColor("tidal", "primary") ?? "#6b7280",
        capacityGW: gw("tidal"),
        density: null,
        areaKm2: null,
      },
      {
        key: "hydro",
        name: "Hydropower Plants",
        color: getEnergyColor("hydro", "primary") ?? "#6b7280",
        capacityGW: gw("hydro"),
        density: null,
        areaKm2: null,
      },
      {
        key: "biomass",
        name: "Biomass Power Stations",
        color: getEnergyColor("biomass", "primary") ?? "#6b7280",
        capacityGW: gw("biomass"),
        density: null,
        areaKm2: null,
      },
      {
        key: "nuclear",
        name: "Nuclear Micro Plants",
        color: getEnergyColor("nuclear", "primary") ?? "#6b7280",
        capacityGW: gw("nuclear"),
        density: null,
        areaKm2: null,
      },
    ];

    // keep only techs that actually exist in the query results (non-zero or explicitly present)
    const present = new Set(techList);
    return rows.filter(r => present.has(r.key) && (r.capacityGW ?? 0) > 0);
  }, [labels, byTech, techList]);

    const GM_AREA_KM2 = 1276;
const totals = useMemo(() => {
    const totalGW   = data.reduce((s, r) => s + (r.capacityGW || 0), 0);
    const totalArea = data.reduce((s, r) => s + (r.areaKm2 || 0), 0);

    // land = solar + onshore; sea = offshore
    const landArea = data
      .filter(r => r.key === "solar" || r.key === "onshoreWind")
      .reduce((s, r) => s + (r.areaKm2 || 0), 0);
    const seaArea = data
      .filter(r => r.key === "offshoreWind")
      .reduce((s, r) => s + (r.areaKm2 || 0), 0);

    const gmTimes      = totalArea > 0 ? totalArea / GM_AREA_KM2 : 0;
    const landGmTimes  = landArea  > 0 ? landArea  / GM_AREA_KM2 : 0;
    const seaGmTimes   = seaArea   > 0 ? seaArea   / GM_AREA_KM2 : 0;

    // return both “Times” plus raw areas; also alias to *GmTotal if you prefer that name
    return {
      totalGW, totalArea, gmTimes,
      landArea, seaArea,
      landGmTimes, seaGmTimes,
      landGmTotal: landGmTimes,
      seaGmTotal:  seaGmTimes,
    };
  }, [data]);

  const fmt = {
    i0: (n) => Math.round(n || 0).toLocaleString(),
    f1: (n) => (Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 1 }),
    f1nz: (n) => (n ? (Number(n)).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—"),
  };



  if (loading) return <div className="text-xs text-gray-600">Loading infrastructure…</div>;
  if (error)   return <div className="text-xs text-red-600">Error: {error.message}</div>;
  if (!data.length) return <div className="text-xs text-gray-600">No capacity rows for {year}.</div>;

  return (
    <div className={className}>
      <h4 className="text-xs font-medium text-blue-800 mb-3">{title}</h4>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-blue-100/60">
            <tr>
              <th className="px-3 py-2 text-left">Technology</th>
              <th className="px-3 py-2 text-right">Capacity (GW)</th>
              <th className="px-3 py-2 text-right">Capacity (MW)</th>
              <th className="px-3 py-2 text-right">Density (MW/km²)</th>
              <th className="px-3 py-2 text-right">Estimated area (km²)</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {data.map((r) => {
              const mw = r.capacityGW * 1000;
              const pct = totals.totalGW > 0 ? Math.round((r.capacityGW / totals.totalGW) * 100) : 0;
              return (
                <tr key={r.key} className="border-t border-gray-200">
                  <td className="px-3 py-2">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-left"
                      style={{ backgroundColor: r.color }}
                    />
                    <span className="align-middle">{r.name}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-medium">{fmt.f1(r.capacityGW)}</td>
                  <td className="px-3 py-2 text-right">{fmt.i0(mw)}</td>
                  <td className="px-3 py-2 text-right">
                    {r.density ? fmt.f1(r.density) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.areaKm2 != null ? fmt.f1(r.areaKm2) : "—"}{r.areaNote ? <span className="text-[10px] text-gray-500"> ({r.areaNote})</span> : null}
                  </td>
                </tr>
              );
            })}

            <tr className="border-t-2 border-blue-200 bg-blue-50/40">
              <td className="px-3 py-2 font-semibold">Total</td>
              <td className="px-3 py-2 text-right font-semibold">{fmt.f1(totals.totalGW)}</td>
              <td className="px-3 py-2 text-right font-semibold">{fmt.i0(totals.totalGW * 1000)}</td>
              <td className="px-3 py-2 text-right text-gray-400">—</td>
              <td className="px-3 py-2 text-right font-semibold">{fmt.f1(totals.totalArea)}</td>
            </tr>
            <tr className="border-t border-blue-200 bg-blue-50/30">
              <td className="px-3 py-2" colSpan={4}>
                How many times the size of Greater Manchester (1,276&nbsp;km²)?
              </td>
              <td className="px-3 py-2 text-right font-semibold">
                {fmt.f1(totals.gmTimes)}×
              </td>
            </tr>
               <tr className="border-t border-blue-200 bg-blue-50/30">
              <td className="px-3 py-2" colSpan={4}>
                How many times the size of Greater Manchester (1,276&nbsp;km²) is needed on land?
              </td>
              <td className="px-3 py-2 text-right font-semibold">
                {fmt.f1(totals.landGmTimes)}×
              </td>
            </tr>
               <tr className="border-t border-blue-200 bg-blue-50/30">
              <td className="px-3 py-2" colSpan={4}>
                How many times the size of Greater Manchester (1,276&nbsp;km²) is needed at sea?
              </td>
              <td className="px-3 py-2 text-right font-semibold">
                {fmt.f1(totals.seaGmTimes)}×
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-[10px] text-blue-900/70">
        Assumptions: Solar PV 40&nbsp;MW/km², Onshore wind 3&nbsp;MW/km², Offshore wind 5&nbsp;MW/km². Offshore area refers to sea footprint.
      </p>
    </div>
  );
}

